import type { Contract, ThemeData } from '../types';
import type { SeededRandom } from '../rng/SeededRandom';

let contractIdCounter = 0;

export function resetContractIdCounter(): void {
  contractIdCounter = 0;
}

/** Set counter to max existing ID so new contracts get unique IDs (call after load). */
export function syncContractIdCounter(contracts: { id: string }[]): void {
  let max = 0;
  for (const c of contracts) {
    const n = parseInt(c.id.replace('contract_', ''), 10);
    if (n > max) max = n;
  }
  contractIdCounter = max;
}

export function generateContract(rng: SeededRandom, themeData: ThemeData, companyLevel: number): Contract {
  const difficulty = rng.nextInt(1, Math.min(companyLevel + 1, 5));
  const stats = Object.keys(themeData.entities.staffRoles[0]!.baseStats);
  const requiredStat = rng.pick(stats);
  const clientName = rng.pick(themeData.names.contractClients);

  const baseDuration = 70; // 1 week
  const durationTicks = baseDuration * rng.nextInt(1, Math.min(difficulty, 3));
  const reward = Math.round(1000 * difficulty * rng.nextFloat(0.8, 1.2) * (1 + companyLevel * 0.1));
  const requiredStaff = Math.min(difficulty, 4);

  return {
    id: `contract_${++contractIdCounter}`,
    clientName,
    description: `${requiredStat} work for ${clientName}`,
    requiredStat,
    difficulty,
    requiredStaff,
    durationTicks,
    reward,
    reputationReward: difficulty,
    deadline: 70 * 4, // Available for 4 weeks before expiring from pool
    deadlineTicks: durationTicks * 3, // Must complete within 3x the expected duration
    failurePenalty: Math.round(reward * 0.5), // Lose half the reward value as penalty
    assignedStaffIds: [],
    lockedStaffIds: [],
    assignmentMode: 'idle' as const,
    progress: 0,
    isActive: false,
  };
}

export function generateContractPool(
  rng: SeededRandom,
  themeData: ThemeData,
  companyLevel: number,
): Contract[] {
  // Pool size scales inversely with company level
  let poolSize: number;
  if (companyLevel <= 3) poolSize = rng.nextInt(3, 4);
  else if (companyLevel <= 6) poolSize = rng.nextInt(2, 3);
  else if (companyLevel <= 9) poolSize = rng.nextInt(1, 2);
  else poolSize = rng.nextBool(0.5) ? 1 : 0;

  const contracts: Contract[] = [];
  for (let i = 0; i < poolSize; i++) {
    contracts.push(generateContract(rng, themeData, companyLevel));
  }
  return contracts;
}
